import React, { useState, useEffect } from 'react';
import {
  PaginationState,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Loader2, Plus } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

// Import services
import { 
  listAccountTypes 
} from '@/app/services/accountTypeService';
import {
  listAccountGroups,
  getAccountGroup,
  createAccountGroup,
  updateAccountGroup,
  deleteAccountGroup
} from '@/app/services/accountGroupService';
import {
  listAccounts,
  getAccount,
  createAccount,
  updateAccount,
  deleteAccount
} from '@/app/services/accountService';
import { 
  listTaxComponentAccounts, 
  createTaxComponentAccount, 
  updateTaxComponentAccount, 
  deleteTaxComponentAccount 
} from '@/app/services/taxComponentAccountService';
import { listTaxComponents } from '@/app/services/taxComponentService';

// Import types
import { 
  AccountGroupResponse, 
  AccountResponse, 
  TaxComponentAccountResponse,
  AccountCreate,
  AccountUpdate,
  AccountGroupCreate,
  AccountGroupUpdate,
  TaxComponentAccountCreate,
  TaxComponentAccountUpdate
} from '@/app/types';

// Import components
import { columns as accountTypeColumns } from './components/columnsAccountType';
import { columns as accountGroupColumns } from './components/columnsAccountGroup';
import { columns as accountColumns } from './components/columnsAccount';
import { columns as taxComponentAccountColumns } from './components/columnsTaxComponentAccount';

import AccountTypeDataTable from './components/AccountTypeDataTable';
import AccountDataTable from './components/AccountDataTable';
import AccountGroupDataTable from './components/AccountGroupDataTable';
import TaxComponentAccountDataTable from './components/TaxComponentAccountDataTable';
import AccountForm from './components/AccountForm';
import AccountGroupForm from './components/AccountGroupForm';
import TaxComponentAccountForm from './components/TaxComponentAccountForm';
import ChartOfAccountsTree from './components/ChartOfAccountsTree';

// Default pagination state
const defaultPagination: PaginationState = {
  pageIndex: 0,
  pageSize: 10,
};

const ChartOfAccountsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('ledger');

  // State for prefilled form values (from tree operations)
  const [prefilledAccountGroupId, setPrefilledAccountGroupId] = useState<number | undefined>(undefined);
  const [prefilledAccountTypeId, setPrefilledAccountTypeId] = useState<number | undefined>(undefined);
  const [prefilledParentGroupId, setPrefilledParentGroupId] = useState<number | undefined>(undefined);

  // States for each tab
  // 1. Account Types Tab
  const [accountTypePagination, setAccountTypePagination] = useState<PaginationState>(defaultPagination);
  const [accountTypeSorting, setAccountTypeSorting] = useState<SortingState>([]);
  const [accountTypeFilters, setAccountTypeFilters] = useState<ColumnFiltersState>([]);
  const [accountTypeSearch, setAccountTypeSearch] = useState('');

  // 2. Account Groups Tab
  const [accountGroupPagination, setAccountGroupPagination] = useState<PaginationState>(defaultPagination);
  const [accountGroupSorting, setAccountGroupSorting] = useState<SortingState>([]);
  const [accountGroupFilters, setAccountGroupFilters] = useState<ColumnFiltersState>([]);
  const [accountGroupSearch, setAccountGroupSearch] = useState('');
  const [accountGroupFormOpen, setAccountGroupFormOpen] = useState(false);
  const [editingAccountGroup, setEditingAccountGroup] = useState<AccountGroupResponse | null>(null);
  const [deletingAccountGroup, setDeletingAccountGroup] = useState<AccountGroupResponse | null>(null);
  const [deleteAccountGroupDialogOpen, setDeleteAccountGroupDialogOpen] = useState(false);

  // 3. Accounts Tab
  const [accountPagination, setAccountPagination] = useState<PaginationState>(defaultPagination);
  const [accountSorting, setAccountSorting] = useState<SortingState>([]);
  const [accountFilters, setAccountFilters] = useState<ColumnFiltersState>([]);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountFormOpen, setAccountFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<AccountResponse | null>(null);
  const [deletingAccount, setDeletingAccount] = useState<AccountResponse | null>(null);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);

  // 4. Tax Component Accounts Tab
  const [taxComponentAccountPagination, setTaxComponentAccountPagination] = useState<PaginationState>(defaultPagination);
  const [taxComponentAccountSorting, setTaxComponentAccountSorting] = useState<SortingState>([]);
  const [taxComponentAccountFilters, setTaxComponentAccountFilters] = useState<ColumnFiltersState>([]);
  const [taxComponentAccountSearch, setTaxComponentAccountSearch] = useState('');
  const [taxComponentAccountFormOpen, setTaxComponentAccountFormOpen] = useState(false);
  const [editingTaxComponentAccount, setEditingTaxComponentAccount] = useState<TaxComponentAccountResponse | null>(null);
  const [deletingTaxComponentAccount, setDeletingTaxComponentAccount] = useState<TaxComponentAccountResponse | null>(null);
  const [deleteTaxComponentAccountDialogOpen, setDeleteTaxComponentAccountDialogOpen] = useState(false);

  // Event listeners for UI events
  useEffect(() => {
    // Account Group events
    const handleEditAccountGroup = (e: CustomEvent<AccountGroupResponse>): void => {
      setEditingAccountGroup(e.detail);
      setAccountGroupFormOpen(true);
    };

    const handleDeleteAccountGroup = (e: CustomEvent<AccountGroupResponse>): void => {
      setDeletingAccountGroup(e.detail);
      setDeleteAccountGroupDialogOpen(true);
    };

    // Account events
    const handleEditAccount = (e: CustomEvent<AccountResponse>): void => {
      setEditingAccount(e.detail);
      setAccountFormOpen(true);
    };

    const handleDeleteAccount = (e: CustomEvent<AccountResponse>): void => {
      setDeletingAccount(e.detail);
      setDeleteAccountDialogOpen(true);
    };

    // Tax Component Account events
    const handleEditTaxComponentAccount = (e: CustomEvent<TaxComponentAccountResponse>): void => {
      setEditingTaxComponentAccount(e.detail);
      setTaxComponentAccountFormOpen(true);
    };

    const handleDeleteTaxComponentAccount = (e: CustomEvent<TaxComponentAccountResponse>): void => {
      setDeletingTaxComponentAccount(e.detail);
      setDeleteTaxComponentAccountDialogOpen(true);
    };

    // Register event listeners
    document.addEventListener('edit-account-group', handleEditAccountGroup as EventListener);
    document.addEventListener('delete-account-group', handleDeleteAccountGroup as EventListener);
    document.addEventListener('edit-account', handleEditAccount as EventListener);
    document.addEventListener('delete-account', handleDeleteAccount as EventListener);
    document.addEventListener('edit-tax-component-account', handleEditTaxComponentAccount as EventListener);
    document.addEventListener('delete-tax-component-account', handleDeleteTaxComponentAccount as EventListener);

    // Cleanup
    return () => {
      document.removeEventListener('edit-account-group', handleEditAccountGroup as EventListener);
      document.removeEventListener('delete-account-group', handleDeleteAccountGroup as EventListener);
      document.removeEventListener('edit-account', handleEditAccount as EventListener);
      document.removeEventListener('delete-account', handleDeleteAccount as EventListener);
      document.removeEventListener('edit-tax-component-account', handleEditTaxComponentAccount as EventListener);
      document.removeEventListener('delete-tax-component-account', handleDeleteTaxComponentAccount as EventListener);
    };
  }, []);

  // API Queries

  // 1. Account Types - only fetch when types tab is active
  const {
    data: accountTypesData = { items: [], total: 0 },
    isLoading: isLoadingAccountTypes,
  } = useQuery({
    queryKey: [
      'accountTypes',
      {
        pagination: accountTypePagination,
        sorting: accountTypeSorting,
        filters: accountTypeFilters,
        search: accountTypeSearch,
      },
    ],
    queryFn: () => listAccountTypes({
      skip: accountTypePagination.pageIndex * accountTypePagination.pageSize,
      limit: accountTypePagination.pageSize,
      sort_by: accountTypeSorting.length > 0 ? accountTypeSorting[0].id : undefined,
      sort_dir: accountTypeSorting.length > 0 ? (accountTypeSorting[0].desc ? 'desc' : 'asc') : undefined,
      search_query: accountTypeSearch || undefined,
      filters: '[]', // Prevent backend warning
    }),
    enabled: activeTab === 'types' || activeTab === 'ledger' || accountGroupFormOpen, // Fetch when types tab is active, ledger tree is showing, or when account group form needs it
  });

  // 2. Account Groups - only fetch when groups tab is active or when needed for account form or ledger tree
  const shouldFetchAccountGroups = activeTab === 'groups' || activeTab === 'accounts' || activeTab === 'ledger' || accountFormOpen;
  const {
    data: accountGroupsData = { items: [], total: 0 },
    isLoading: isLoadingAccountGroups,
  } = useQuery({
    queryKey: [
      'accountGroups',

      {
        pagination: accountGroupPagination,
        sorting: accountGroupSorting,
        filters: accountGroupFilters,
        search: accountGroupSearch,
      },
    ],
    queryFn: () => listAccountGroups({
      skip: accountGroupPagination.pageIndex * accountGroupPagination.pageSize,
      limit: accountGroupPagination.pageSize,
      sort_by: accountGroupSorting.length > 0 ? accountGroupSorting[0].id : undefined,
      sort_dir: accountGroupSorting.length > 0 ? (accountGroupSorting[0].desc ? 'desc' : 'asc') : undefined,
      search_query: accountGroupSearch || undefined,
      filters: '[]', // Prevent backend warning
    }),
    enabled: shouldFetchAccountGroups, // Only fetch when needed
  });

  // Fetch ALL account groups for form dropdowns (not paginated)
  const {
    data: allAccountGroupsData = { items: [], total: 0 },
  } = useQuery({
    queryKey: ['accountGroups', 'all'],
    queryFn: () => listAccountGroups({
      limit: 500,
      sort_by: 'name',
      sort_dir: 'asc',
      filters: '[]',
    }),
    enabled: accountFormOpen || accountGroupFormOpen, // Only fetch when forms are open
  });

  const createAccountGroupMutation = useMutation({
    mutationFn: (newAccountGroup: AccountGroupCreate) => createAccountGroup(newAccountGroup),
    onSuccess: () => {
      toast.success('Account group created successfully');
      queryClient.invalidateQueries({ queryKey: ['accountGroups'] });
      setAccountGroupFormOpen(false);
    },
    onError: (error) => {
      toast.error('Failed to create account group');
      console.error('Error creating account group:', error);
    },
  });

  const updateAccountGroupMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AccountGroupUpdate }) => 
      updateAccountGroup(id, data),
    onSuccess: () => {
      toast.success('Account group updated successfully');
      queryClient.invalidateQueries({ queryKey: ['accountGroups'] });
      setAccountGroupFormOpen(false);
      setEditingAccountGroup(null);
    },
    onError: (error) => {
      toast.error('Failed to update account group');
      console.error('Error updating account group:', error);
    },
  });

  const deleteAccountGroupMutation = useMutation({
    mutationFn: (id: number) => deleteAccountGroup(id),
    onSuccess: () => {
      toast.success('Account group deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['accountGroups'] });
      setDeleteAccountGroupDialogOpen(false);
      setDeletingAccountGroup(null);
    },
    onError: (error) => {
      toast.error('Failed to delete account group. It may be in use by accounts.');
      console.error('Error deleting account group:', error);
    },
  });

  // 3. Accounts - only fetch when accounts tab is active or when needed for tax mapping or ledger tree
  const shouldFetchAccounts = activeTab === 'accounts' || activeTab === 'tax-mapping' || activeTab === 'ledger' || taxComponentAccountFormOpen;
  const {
    data: accountsData = { items: [], total: 0 },
    isLoading: isLoadingAccounts,
  } = useQuery({
    queryKey: [
      'accounts',

      {
        pagination: accountPagination,
        sorting: accountSorting,
        filters: accountFilters,
        search: accountSearch,
      },
    ],
    queryFn: () => listAccounts({
      skip: accountPagination.pageIndex * accountPagination.pageSize,
      limit: accountPagination.pageSize,
      sort_by: accountSorting.length > 0 ? accountSorting[0].id : undefined,
      sort_dir: accountSorting.length > 0 ? (accountSorting[0].desc ? 'desc' : 'asc') : undefined,
      search_query: accountSearch || undefined,
      filters: '[]', // Prevent backend warning
    }),
    enabled: shouldFetchAccounts, // Only fetch when needed
  });

  const createAccountMutation = useMutation({
    mutationFn: (newAccount: AccountCreate) => createAccount(newAccount),
    onSuccess: () => {
      toast.success('Account created successfully');
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setAccountFormOpen(false);
    },
    onError: (error) => {
      toast.error('Failed to create account');
      console.error('Error creating account:', error);
    },
  });

  const updateAccountMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AccountUpdate }) => 
      updateAccount(id, data),
    onSuccess: () => {
      toast.success('Account updated successfully');
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setAccountFormOpen(false);
      setEditingAccount(null);
    },
    onError: (error) => {
      toast.error('Failed to update account');
      console.error('Error updating account:', error);
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: (id: number) => deleteAccount(id),
    onSuccess: () => {
      toast.success('Account deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setDeleteAccountDialogOpen(false);
      setDeletingAccount(null);
    },
    onError: (error) => {
      toast.error('Failed to delete account. It may be in use.');
      console.error('Error deleting account:', error);
    },
  });

  // 4. Tax Components for the Tax Component Accounts tab - only fetch when tax-mapping tab is active
  const shouldFetchTaxComponents = activeTab === 'tax-mapping' || taxComponentAccountFormOpen;
  const {
    data: taxComponentsData = { items: [], total: 0 },
  } = useQuery({
    queryKey: ['taxComponents'],
    queryFn: () => listTaxComponents({
      skip: 0,
      limit: 100, // Fetch a large number for dropdown
      filters: '[]', // Prevent backend warning
    }),
    enabled: shouldFetchTaxComponents, // Only fetch when needed
  });

  // 5. Tax Component Accounts - only fetch when tax-mapping tab is active
  const {
    data: taxComponentAccountsData = { items: [], total: 0 },
    isLoading: isLoadingTaxComponentAccounts,
  } = useQuery({
    queryKey: [
      'taxComponentAccounts',

      {
        pagination: taxComponentAccountPagination,
        sorting: taxComponentAccountSorting,
        filters: taxComponentAccountFilters,
        search: taxComponentAccountSearch,
      },
    ],
    queryFn: () => listTaxComponentAccounts({
      skip: taxComponentAccountPagination.pageIndex * taxComponentAccountPagination.pageSize,
      limit: taxComponentAccountPagination.pageSize,
      sort_by: taxComponentAccountSorting.length > 0 ? taxComponentAccountSorting[0].id : undefined,
      sort_dir: taxComponentAccountSorting.length > 0 ? (taxComponentAccountSorting[0].desc ? 'desc' : 'asc') : undefined,
      search_query: taxComponentAccountSearch || undefined,
      filters: '[]', // Prevent backend warning
    }),
    enabled: activeTab === 'tax-mapping', // Only fetch when this tab is active
  });

  const createTaxComponentAccountMutation = useMutation({
    mutationFn: (newMapping: TaxComponentAccountCreate) => 
      createTaxComponentAccount(newMapping),
    onSuccess: () => {
      toast.success('Tax component account mapping created successfully');
      queryClient.invalidateQueries({ queryKey: ['taxComponentAccounts'] });
      setTaxComponentAccountFormOpen(false);
    },
    onError: (error) => {
      toast.error('Failed to create tax component account mapping');
      console.error('Error creating tax component account mapping:', error);
    },
  });

  const updateTaxComponentAccountMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TaxComponentAccountUpdate }) => 
      updateTaxComponentAccount(id, data),
    onSuccess: () => {
      toast.success('Tax component account mapping updated successfully');
      queryClient.invalidateQueries({ queryKey: ['taxComponentAccounts'] });
      setTaxComponentAccountFormOpen(false);
      setEditingTaxComponentAccount(null);
    },
    onError: (error) => {
      toast.error('Failed to update tax component account mapping');
      console.error('Error updating tax component account mapping:', error);
    },
  });

  const deleteTaxComponentAccountMutation = useMutation({
    mutationFn: (id: number) => deleteTaxComponentAccount(id),
    onSuccess: () => {
      toast.success('Tax component account mapping deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['taxComponentAccounts'] });
      setDeleteTaxComponentAccountDialogOpen(false);
      setDeletingTaxComponentAccount(null);
    },
    onError: (error) => {
      toast.error('Failed to delete tax component account mapping');
      console.error('Error deleting tax component account mapping:', error);
    },
  });

  // Handle form submissions
  
  const handleAccountGroupSubmit = (data: AccountGroupCreate | AccountGroupUpdate): void => {
    if (editingAccountGroup) {
      updateAccountGroupMutation.mutate({
        id: editingAccountGroup.id,
        data: data as AccountGroupUpdate,
      });
    } else {
      createAccountGroupMutation.mutate(data as AccountGroupCreate);
    }
  };

  const handleAccountSubmit = (data: AccountCreate | AccountUpdate): void => {
    if (editingAccount) {
      updateAccountMutation.mutate({
        id: editingAccount.id,
        data: data as AccountUpdate,
      });
    } else {
      createAccountMutation.mutate(data as AccountCreate);
    }
  };

  const handleTaxComponentAccountSubmit = (data: TaxComponentAccountCreate | TaxComponentAccountUpdate): void => {
    if (editingTaxComponentAccount) {
      updateTaxComponentAccountMutation.mutate({
        id: editingTaxComponentAccount.id,
        data: data as TaxComponentAccountUpdate,
      });
    } else {
      createTaxComponentAccountMutation.mutate(data as TaxComponentAccountCreate);
    }
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Chart of Accounts</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="ledger">Ledger</TabsTrigger>
          <TabsTrigger value="accounts">Accounts</TabsTrigger>
          <TabsTrigger value="groups">Account Groups</TabsTrigger>
          <TabsTrigger value="types">Account Types</TabsTrigger>
          <TabsTrigger value="tax-mapping">Tax Mapping</TabsTrigger>
        </TabsList>

        {/* Ledger Tree Tab */}
        <TabsContent value="ledger" className="py-4">
          <div className="bg-white rounded-md shadow h-[calc(100vh-280px)] min-h-[500px]">
            <ChartOfAccountsTree
              onCreateAccount={(prefilledGroupId) => {
                setEditingAccount(null);
                setPrefilledAccountGroupId(prefilledGroupId);
                setAccountFormOpen(true);
              }}
              onEditAccount={async (accountId) => {
                // Look in paginated data first, then fetch by ID
                let account = accountsData.items.find(a => a.id === accountId);
                if (!account) {
                  // Fetch by ID for items not in paginated view
                  try {
                    account = await getAccount(accountId);
                  } catch (error) {
                    console.error('Failed to fetch account:', error);
                    toast.error('Failed to load account');
                    return;
                  }
                }
                if (account) {
                  setEditingAccount(account);
                  setPrefilledAccountGroupId(undefined);
                  setAccountFormOpen(true);
                }
              }}
              onDeleteAccount={async (accountId) => {
                // Look in paginated data first, then fetch by ID
                let account = accountsData.items.find(a => a.id === accountId);
                if (!account) {
                  try {
                    account = await getAccount(accountId);
                  } catch (error) {
                    console.error('Failed to fetch account:', error);
                    toast.error('Failed to load account');
                    return;
                  }
                }
                if (account) {
                  setDeletingAccount(account);
                  setDeleteAccountDialogOpen(true);
                }
              }}
              onCreateAccountGroup={(prefilledTypeId, prefilledParentId) => {
                setEditingAccountGroup(null);
                setPrefilledAccountTypeId(prefilledTypeId);
                setPrefilledParentGroupId(prefilledParentId);
                setAccountGroupFormOpen(true);
              }}
              onEditAccountGroup={async (groupId) => {
                // Look in paginated data first, then fetch by ID
                let group = accountGroupsData.items.find(g => g.id === groupId);
                if (!group) {
                  try {
                    group = await getAccountGroup(groupId);
                  } catch (error) {
                    console.error('Failed to fetch account group:', error);
                    toast.error('Failed to load account group');
                    return;
                  }
                }
                if (group) {
                  setEditingAccountGroup(group);
                  setPrefilledAccountTypeId(undefined);
                  setPrefilledParentGroupId(undefined);
                  setAccountGroupFormOpen(true);
                }
              }}
              onDeleteAccountGroup={async (groupId) => {
                // Look in paginated data first, then fetch by ID
                let group = accountGroupsData.items.find(g => g.id === groupId);
                if (!group) {
                  try {
                    group = await getAccountGroup(groupId);
                  } catch (error) {
                    console.error('Failed to fetch account group:', error);
                    toast.error('Failed to load account group');
                    return;
                  }
                }
                if (group) {
                  setDeletingAccountGroup(group);
                  setDeleteAccountGroupDialogOpen(true);
                }
              }}
            />
          </div>
        </TabsContent>

        {/* Accounts Tab */}
        <TabsContent value="accounts" className="py-4">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => {
                setEditingAccount(null);
                setAccountFormOpen(true);
              }}
              className="flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Account
            </Button>
          </div>

          {accountsData && (
            <div className="bg-white rounded-md shadow">
              {/* Account DataTable */}
              <AccountDataTable 
                data={accountsData}
                columns={accountColumns}
                isLoading={isLoadingAccounts}
                pagination={accountPagination}
                sorting={accountSorting}
                columnFilters={accountFilters}
                searchValue={accountSearch}
                onSearchChange={setAccountSearch}
                onPaginationChange={setAccountPagination}
                onSortingChange={setAccountSorting}
                onColumnFiltersChange={setAccountFilters}
              />
            </div>
          )}

        </TabsContent>

        {/* Account Groups Tab */}
        <TabsContent value="groups" className="py-4">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => {
                setEditingAccountGroup(null);
                setAccountGroupFormOpen(true);
              }}
              className="flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Account Group
            </Button>
          </div>

          {accountGroupsData && (
            <div className="bg-white rounded-md shadow">
              {/* Account Group DataTable */}
              <AccountGroupDataTable 
                data={accountGroupsData}
                columns={accountGroupColumns}
                isLoading={isLoadingAccountGroups}
                pagination={accountGroupPagination}
                sorting={accountGroupSorting}
                columnFilters={accountGroupFilters}
                searchValue={accountGroupSearch}
                onSearchChange={setAccountGroupSearch}
                onPaginationChange={setAccountGroupPagination}
                onSortingChange={setAccountGroupSorting}
                onColumnFiltersChange={setAccountGroupFilters}
              />
            </div>
          )}

        </TabsContent>

        {/* Account Types Tab (Read Only) */}
        <TabsContent value="types" className="py-4">
          {accountTypesData && (
            <div className="bg-white rounded-md shadow">
              <AccountTypeDataTable
                data={accountTypesData}
                columns={accountTypeColumns}
                isLoading={isLoadingAccountTypes}
                pagination={accountTypePagination}
                sorting={accountTypeSorting}
                columnFilters={accountTypeFilters}
                searchValue={accountTypeSearch}
                onSearchChange={setAccountTypeSearch}
                onPaginationChange={setAccountTypePagination}
                onSortingChange={setAccountTypeSorting}
                onColumnFiltersChange={setAccountTypeFilters}
              />
            </div>
          )}
        </TabsContent>

        {/* Tax Component Account Mapping Tab */}
        <TabsContent value="tax-mapping" className="py-4">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => {
                setEditingTaxComponentAccount(null);
                setTaxComponentAccountFormOpen(true);
              }}
              className="flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Tax Mapping
            </Button>
          </div>

          {taxComponentAccountsData && (
            <div className="bg-white rounded-md shadow">
              {/* Tax Component Account DataTable */}
              <TaxComponentAccountDataTable
                data={taxComponentAccountsData}
                columns={taxComponentAccountColumns}
                isLoading={isLoadingTaxComponentAccounts}
                pagination={taxComponentAccountPagination}
                sorting={taxComponentAccountSorting}
                columnFilters={taxComponentAccountFilters}
                searchValue={taxComponentAccountSearch}
                onSearchChange={setTaxComponentAccountSearch}
                onPaginationChange={setTaxComponentAccountPagination}
                onSortingChange={setTaxComponentAccountSorting}
                onColumnFiltersChange={setTaxComponentAccountFilters}
              />
            </div>
          )}

          {/* Tax Component Account Form Dialog */}
          <TaxComponentAccountForm
            open={taxComponentAccountFormOpen}
            onOpenChange={setTaxComponentAccountFormOpen}
            onSubmit={handleTaxComponentAccountSubmit}
            defaultValues={editingTaxComponentAccount || undefined}
            isLoading={
              createTaxComponentAccountMutation.isPending ||
              updateTaxComponentAccountMutation.isPending
            }
            mode={editingTaxComponentAccount ? 'edit' : 'create'}
            accounts={accountsData.items}
            taxComponents={taxComponentsData.items}
          />

          {/* Delete Tax Component Account Confirmation Dialog */}
          <AlertDialog
            open={deleteTaxComponentAccountDialogOpen}
            onOpenChange={setDeleteTaxComponentAccountDialogOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Tax Component Account Mapping</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this tax component to account mapping? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={deleteTaxComponentAccountMutation.isPending}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={deleteTaxComponentAccountMutation.isPending}
                  onClick={() => {
                    if (deletingTaxComponentAccount) {
                      deleteTaxComponentAccountMutation.mutate(deletingTaxComponentAccount.id);
                    }
                  }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleteTaxComponentAccountMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>
      </Tabs>

      {/* Account Form Dialog - Rendered at page level so it can be opened from any tab */}
      <AccountForm
        open={accountFormOpen}
        onOpenChange={(open) => {
          setAccountFormOpen(open);
          if (!open) {
            setPrefilledAccountGroupId(undefined);
            setEditingAccount(null);
          }
        }}
        onSubmit={handleAccountSubmit}
        defaultValues={editingAccount || (prefilledAccountGroupId ? { account_group_id: prefilledAccountGroupId } : undefined)}
        isLoading={createAccountMutation.isPending || updateAccountMutation.isPending}
        mode={editingAccount ? 'edit' : 'create'}
        accountGroups={allAccountGroupsData.items}
        lockedAccountGroupId={!editingAccount ? prefilledAccountGroupId : undefined}
      />

      {/* Delete Account Confirmation Dialog */}
      <AlertDialog
        open={deleteAccountDialogOpen}
        onOpenChange={setDeleteAccountDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the account &quot;{deletingAccount?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAccountMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteAccountMutation.isPending}
              onClick={() => {
                if (deletingAccount) {
                  deleteAccountMutation.mutate(deletingAccount.id);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteAccountMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Account Group Form Dialog - Rendered at page level so it can be opened from any tab */}
      <AccountGroupForm
        open={accountGroupFormOpen}
        onOpenChange={(open) => {
          setAccountGroupFormOpen(open);
          if (!open) {
            setPrefilledAccountTypeId(undefined);
            setPrefilledParentGroupId(undefined);
            setEditingAccountGroup(null);
          }
        }}
        onSubmit={handleAccountGroupSubmit}
        defaultValues={editingAccountGroup || (prefilledAccountTypeId || prefilledParentGroupId ? {
          account_type_id: prefilledAccountTypeId,
          parent_id: prefilledParentGroupId,
        } : undefined)}
        isLoading={createAccountGroupMutation.isPending || updateAccountGroupMutation.isPending}
        mode={editingAccountGroup ? 'edit' : 'create'}
        accountTypes={accountTypesData.items}
        accountGroups={allAccountGroupsData.items}
        currentAccountGroupId={editingAccountGroup?.id}
      />

      {/* Delete Account Group Confirmation Dialog */}
      <AlertDialog
        open={deleteAccountGroupDialogOpen}
        onOpenChange={setDeleteAccountGroupDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the account group &quot;{deletingAccountGroup?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAccountGroupMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteAccountGroupMutation.isPending}
              onClick={() => {
                if (deletingAccountGroup) {
                  deleteAccountGroupMutation.mutate(deletingAccountGroup.id);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteAccountGroupMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ChartOfAccountsPage; 